// © 2026 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {formatPriceInput, parsePriceInput} from '../../helpers/decimal-input-helper';

/** Shown under the box when it is empty after the user touched it. */
export const PRICE_REQUIRED_MESSAGE = 'Bitte geben Sie einen Preis ein.';

/** Shown for anything {@link parsePriceInput} refuses: text, three decimals, a thousands separator. */
export const PRICE_INVALID_MESSAGE =
  'Bitte geben Sie einen gültigen Preis ein, z. B. 12,50 (höchstens zwei Nachkommastellen).';

/**
 * One line of context above the box, e.g. „Artikel: Cola 0,3 l" or „Standardpreis: 3,50 €".
 *
 * Give either `text` or `amount`. An `amount` goes through the same `currency: 'EUR'` pipe the price lists use,
 * so the dialog shows a price exactly the way the row the user just clicked shows it.
 */
export interface PriceDialogDetail {
  readonly label: string;
  readonly text?: string;
  readonly amount?: number;
}

/** Keeps the element ids of two dialogs apart (a story rendering several, a test creating one per spec). */
let dialogCounter = 0;

/**
 * Asks for one article price (#809).
 *
 * ### Why this replaced `window.prompt`
 *
 * Both price screens used to ask with `prompt()` and read the answer with `parseFloat`, which stops at the first
 * character it does not understand. A German user types „12,50", `parseFloat` sees „12", and the article was
 * saved at 12 € without a word. The box here reads the answer through {@link parsePriceInput}, which takes a
 * comma or a point. The prompt also could not name what it was about beyond a line of plain text, could not be
 * checked by axe, and blocked the whole tab.
 *
 * ### What the host decides
 *
 * The dialog does no HTTP. It emits {@link saved} with the parsed price, {@link reset} when the optional reset
 * button is pressed, or {@link cancelled}; the host saves, closes and reports. That keeps the two callers'
 * different rules (the event override treats the base price as "no override") out of a shared component.
 *
 * ### Cancelling is always free
 *
 * Nothing is written until „Speichern" or the reset button is pressed, so `Escape`, a backdrop click and
 * „Abbrechen" all just close it (the "cancelling is free" case in the style guide). This matters: the old event
 * override prompt treated Cancel as "remove the override", so dismissing it deleted a price.
 */
@Component({
  selector: 'app-price-input-dialog',
  standalone: true,
  imports: [CurrencyPipe],
  template: `
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         data-testid="price-dialog-backdrop" (click)="cancel()">
      <div class="flex max-h-[100dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl
                  sm:max-h-[calc(100dvh-2rem)] sm:max-w-md sm:rounded-2xl"
           role="dialog" aria-modal="true" [attr.aria-labelledby]="headingId" (click)="$event.stopPropagation()">
        <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <h2 [id]="headingId" class="text-xl font-bold tracking-tight text-gray-900">{{ heading() }}</h2>

          @if (details().length > 0) {
            <dl class="space-y-1 text-sm">
              @for (detail of details(); track detail.label) {
                <div class="flex flex-wrap gap-x-2">
                  <dt class="text-gray-500">{{ detail.label }}:</dt>
                  <dd class="font-semibold text-gray-900">
                    @if (detail.amount !== undefined) {
                      {{ detail.amount | currency: 'EUR' }}
                    } @else {
                      {{ detail.text }}
                    }
                  </dd>
                </div>
              }
            </dl>
          }

          <div>
            <label [attr.for]="inputId" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              {{ label() }}
            </label>
            <!--
              type="text" with inputmode="decimal", never type="number": a number input handles the comma by the
              BROWSER's locale, not the app's, which is how a "0,4" once reached a backend as 4. parsePriceInput
              is the one place a separator is interpreted.
            -->
            <input #box type="text" inputmode="decimal" autocomplete="off" placeholder="0,00" [id]="inputId"
                   [value]="text()" (input)="onInput($event)" (keydown.enter)="save()"
                   [attr.aria-invalid]="showError() || null" [attr.aria-describedby]="showError() ? errorId : hintId"
                   class="block w-full rounded-xl border-[1px] bg-white px-3 py-2.5 text-gray-900 shadow-sm
                          transition-all duration-200 placeholder-gray-300 focus:ring-1 sm:text-sm"
                   [class.border-gray-200]="!showError()"
                   [class.focus:border-theme-primary-500]="!showError()"
                   [class.focus:ring-theme-primary-500]="!showError()"
                   [class.border-red-300]="showError()"
                   [class.focus:border-red-500]="showError()"
                   [class.focus:ring-red-500]="showError()">
            <!--
              The slot always holds two text-xs lines (min-h-8 = 2 x 1rem). The hint takes one line and the longer
              error wraps to two, so without the reserve the card grew when the error appeared and, being centred
              from sm up, jumped by half a line. Not 2lh: that unit needs Safari 16.4, the build floor is iOS 14.5.
            -->
            <div class="mt-2 min-h-8 text-xs" data-testid="price-dialog-message">
              @if (showError()) {
                <p [id]="errorId" class="font-semibold text-red-600" data-testid="price-dialog-error">
                  {{ errorMessage() }}
                </p>
              } @else {
                <p [id]="hintId" class="text-gray-500">Komma oder Punkt als Dezimaltrennzeichen.</p>
              }
            </div>
          </div>
        </div>

        <!--
          Column-reverse on phones so „Speichern" sits on top where the thumb is, and the reset action (if any)
          stays at the bottom, furthest from it. On sm+ „Abbrechen" and „Speichern" share a right-aligned row, and
          the reset action takes a row of its own above them: next to both buttons, „Auf Standardpreis
          zurücksetzen" does not fit a max-w-md card, and „Speichern" was pushed out past the panel's edge.
        -->
        <div class="flex flex-col-reverse gap-3 border-t border-gray-100 p-4
                    sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:px-6">
          @if (resetLabel(); as resetText) {
            <button type="button" (click)="reset.emit()" data-testid="price-dialog-reset"
                    class="inline-flex items-center justify-center rounded-2xl px-3 py-2.5 text-sm font-semibold
                           text-theme-primary-700 transition-all duration-200 hover:bg-theme-primary-50
                           active:scale-95 sm:basis-full sm:justify-start">
              {{ resetText }}
            </button>
          }
          <button type="button" (click)="cancel()" data-testid="price-dialog-cancel"
                  class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4 py-2.5
                         text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                         hover:border-gray-300 hover:bg-gray-50 active:scale-95">
            Abbrechen
          </button>
          <button type="button" (click)="save()" [disabled]="invalid()" data-testid="price-dialog-save"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                         text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                         transition-all duration-200 hover:bg-theme-primary-700 active:scale-95
                         disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
            Speichern
          </button>
        </div>
      </div>
    </div>
  `
})
export class PriceInputDialogComponent {
  /** The dialog title, e.g. „Preis ändern". It is also the dialog's accessible name. */
  readonly heading = input.required<string>();
  /** Context lines naming what the price belongs to. Empty renders no list at all. */
  readonly details = input<readonly PriceDialogDetail[]>([]);
  /** The label of the box. */
  readonly label = input('Preis in €');
  /** The price to pre-fill, shown as „3,50". `null` starts empty. */
  readonly initialValue = input<number | null>(null);
  /**
   * The text of an optional third action that removes the price instead of setting one, e.g. „Auf Standardpreis
   * zurücksetzen". `null` hides it. The dialog only emits {@link reset}; what resetting means is the host's call.
   */
  readonly resetLabel = input<string | null>(null);

  /** The parsed price, emitted only for a valid entry. */
  readonly saved = output<number>();
  /** Escape, backdrop or „Abbrechen". The host must change nothing. */
  readonly cancelled = output<void>();
  /** The reset action was pressed. Only reachable when {@link resetLabel} is set. */
  readonly reset = output<void>();

  private readonly injector = inject(Injector);
  private readonly box = viewChild.required<ElementRef<HTMLInputElement>>('box');

  protected readonly headingId = `price-dialog-heading-${++dialogCounter}`;
  protected readonly inputId = `price-dialog-input-${dialogCounter}`;
  protected readonly errorId = `price-dialog-error-${dialogCounter}`;
  protected readonly hintId = `price-dialog-hint-${dialogCounter}`;

  /**
   * The raw typed text. Kept as typed (never re-formatted per keystroke) so a half-typed „12," is not fought;
   * a `linkedSignal` so it re-seeds if the host swaps {@link initialValue} while the dialog stays open.
   */
  protected readonly text = linkedSignal(() => formatPriceInput(this.initialValue()));

  /**
   * Whether the user has typed or tried to save. An empty box keeps „Speichern" disabled from the start, but
   * only says why once the user has done something, so a dialog opened without a pre-fill does not greet them
   * with an error.
   */
  private readonly touched = signal(false);

  protected readonly invalid = computed(() => parsePriceInput(this.text()) === null);
  protected readonly showError = computed(() => this.touched() && this.invalid());
  protected readonly errorMessage = computed(() =>
    this.text().trim() === '' ? PRICE_REQUIRED_MESSAGE : PRICE_INVALID_MESSAGE);

  constructor() {
    // Focus AND select: the pre-filled price is almost always replaced, not edited, so typing should overwrite
    // it. A modal that leaves focus behind also leaves a keyboard user tabbing through the page underneath.
    afterNextRender(() => {
      const element = this.box().nativeElement;
      element.focus();
      element.select();
    }, {injector: this.injector});
  }

  protected onInput(event: Event): void {
    this.text.set((event.target as HTMLInputElement).value);
    this.touched.set(true);
  }

  protected save(): void {
    const price = parsePriceInput(this.text());
    if (price === null) {
      // The disabled button cannot get here, but Enter in the box can. Show the reason and keep focus on the
      // box, which is what makes a screen reader read the message it now points at.
      this.touched.set(true);
      this.box().nativeElement.focus();
      return;
    }
    this.saved.emit(price);
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
